import { db } from '../../config/firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { AuditService } from './AuditService';

// ─── Diagnosis Types ───

export interface FirebaseDiagnosisRecord {
  id: string;
  code: string;        // ICD-10 code
  name: string;        // Diagnosis name
  diagnosedDate: string;
  diagnosedBy: string; // Provider name for display
  providerId: string;  // Provider UID (for security rules)
  patientId: string;   // Patient ID
  status: 'active' | 'resolved' | 'chronic';
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Prescription Types ───

export interface FirebasePrescriptionRecord {
  id: string;
  medicationName: string;
  dosage: string;
  frequency: string;
  prescribedDate: string;
  prescribedBy: string;  // Provider name for display
  providerId: string;    // Provider UID (for security rules)
  patientId: string;     // Patient ID
  status: 'active' | 'discontinued' | 'completed';
  startDate: string;
  endDate?: string;
  refillsRemaining?: number;
  pharmacy?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Service ───

/** Strip undefined values — Firestore setDoc rejects them */
function stripUndefined(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}

export class FirebaseDiagnosisPrescriptionService {
  private static DIAGNOSES = 'diagnoses';
  private static PRESCRIPTIONS = 'prescriptions';

  // ────────────────────────────────────────
  // DIAGNOSES
  // ────────────────────────────────────────

  static async addDiagnosis(
    data: Omit<FirebaseDiagnosisRecord, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const docRef = doc(collection(db, this.DIAGNOSES));
    await setDoc(docRef, {
      ...stripUndefined(data as any),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    AuditService.logPhiModification(
      data.providerId, 'provider', 'create', 'form' as any, docRef.id, data.patientId,
      `Added diagnosis: ${data.code} - ${data.name}`
    );
    return docRef.id;
  }

  static async updateDiagnosis(
    diagnosisId: string,
    updates: Partial<Pick<FirebaseDiagnosisRecord, 'status' | 'notes'>>,
    providerId: string,
    patientId: string
  ): Promise<void> {
    await updateDoc(doc(db, this.DIAGNOSES, diagnosisId), {
      ...updates,
      updatedAt: Timestamp.now(),
    });
    AuditService.logPhiModification(
      providerId, 'provider', 'update', 'form' as any, diagnosisId, patientId,
      `Updated diagnosis status to: ${updates.status || 'modified'}`
    );
  }

  static async deleteDiagnosis(
    diagnosisId: string,
    providerId: string,
    patientId: string
  ): Promise<void> {
    await deleteDoc(doc(db, this.DIAGNOSES, diagnosisId));
    AuditService.logPhiModification(
      providerId, 'provider', 'delete', 'form' as any, diagnosisId, patientId,
      'Deleted diagnosis record'
    );
  }

  static subscribeToDiagnoses(
    patientId: string,
    providerId: string,
    callback: (diagnoses: FirebaseDiagnosisRecord[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.DIAGNOSES),
      where('patientId', '==', patientId),
      where('providerId', '==', providerId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.diagnosisFromFirestore(d.id, d.data())));
    }, (error) => {
      console.error('Error subscribing to diagnoses:', error);
    });
  }

  /** Client-side: read all diagnoses for a patient (any provider). */
  static subscribeToClientDiagnoses(
    patientId: string,
    callback: (diagnoses: FirebaseDiagnosisRecord[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.DIAGNOSES),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.diagnosisFromFirestore(d.id, d.data())));
    }, (error) => {
      console.error('Error subscribing to client diagnoses:', error);
    });
  }

  // ────────────────────────────────────────
  // PRESCRIPTIONS
  // ────────────────────────────────────────

  static async addPrescription(
    data: Omit<FirebasePrescriptionRecord, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const docRef = doc(collection(db, this.PRESCRIPTIONS));
    await setDoc(docRef, {
      ...stripUndefined(data as any),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    AuditService.logPhiModification(
      data.providerId, 'provider', 'create', 'form' as any, docRef.id, data.patientId,
      `Added prescription: ${data.medicationName} ${data.dosage}`
    );
    return docRef.id;
  }

  static async updatePrescription(
    prescriptionId: string,
    updates: Partial<Pick<FirebasePrescriptionRecord, 'status' | 'notes' | 'endDate' | 'refillsRemaining' | 'dosage' | 'frequency'>>,
    providerId: string,
    patientId: string
  ): Promise<void> {
    await updateDoc(doc(db, this.PRESCRIPTIONS, prescriptionId), {
      ...updates,
      updatedAt: Timestamp.now(),
    });
    AuditService.logPhiModification(
      providerId, 'provider', 'update', 'form' as any, prescriptionId, patientId,
      `Updated prescription`
    );
  }

  static async deletePrescription(
    prescriptionId: string,
    providerId: string,
    patientId: string
  ): Promise<void> {
    await deleteDoc(doc(db, this.PRESCRIPTIONS, prescriptionId));
    AuditService.logPhiModification(
      providerId, 'provider', 'delete', 'form' as any, prescriptionId, patientId,
      'Deleted prescription record'
    );
  }

  static subscribeToPrescriptions(
    patientId: string,
    providerId: string,
    callback: (prescriptions: FirebasePrescriptionRecord[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.PRESCRIPTIONS),
      where('patientId', '==', patientId),
      where('providerId', '==', providerId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.prescriptionFromFirestore(d.id, d.data())));
    }, (error) => {
      console.error('Error subscribing to prescriptions:', error);
    });
  }

  /** Provider-side: read all prescriptions across all patients for this provider. */
  static subscribeToProviderPrescriptions(
    providerId: string,
    callback: (prescriptions: FirebasePrescriptionRecord[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.PRESCRIPTIONS),
      where('providerId', '==', providerId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.prescriptionFromFirestore(d.id, d.data())));
    }, (error) => {
      console.error('Error subscribing to provider prescriptions:', error);
      callback([]);
    });
  }

  /** Client-side: read all prescriptions for a patient (any provider). */
  static subscribeToClientPrescriptions(
    patientId: string,
    callback: (prescriptions: FirebasePrescriptionRecord[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.PRESCRIPTIONS),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.prescriptionFromFirestore(d.id, d.data())));
    }, (error) => {
      console.error('Error subscribing to client prescriptions:', error);
    });
  }

  // ────────────────────────────────────────
  // Firestore Converters
  // ────────────────────────────────────────

  private static diagnosisFromFirestore(id: string, data: any): FirebaseDiagnosisRecord {
    return {
      id,
      code: data.code || '',
      name: data.name || '',
      diagnosedDate: data.diagnosedDate || '',
      diagnosedBy: data.diagnosedBy || '',
      providerId: data.providerId || '',
      patientId: data.patientId || '',
      status: data.status || 'active',
      notes: data.notes,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }

  private static prescriptionFromFirestore(id: string, data: any): FirebasePrescriptionRecord {
    return {
      id,
      medicationName: data.medicationName || '',
      dosage: data.dosage || '',
      frequency: data.frequency || '',
      prescribedDate: data.prescribedDate || '',
      prescribedBy: data.prescribedBy || '',
      providerId: data.providerId || '',
      patientId: data.patientId || '',
      status: data.status || 'active',
      startDate: data.startDate || '',
      endDate: data.endDate,
      refillsRemaining: data.refillsRemaining,
      pharmacy: data.pharmacy,
      notes: data.notes,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }
}
